import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface VaccinationLotLine {
  id: string;
  item_name: string;
}

interface VaccinationLotSubmission {
  sale_order_item_id: string;
  lot_number: string;
  next_application_date: string;
}

interface VaccinationLotPromptDialogProps {
  lines: VaccinationLotLine[];
  submitting: boolean;
  error_text: string | null;
  on_close: () => void;
  on_submit: (lots: VaccinationLotSubmission[]) => void | Promise<void>;
}

export function VaccinationLotPromptDialog({
  lines,
  submitting,
  error_text,
  on_close,
  on_submit,
}: VaccinationLotPromptDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [lots, setLots] = useState<Record<string, string>>({});
  const [next_dates, setNextDates] = useState<Record<string, string>>({});
  const [local_error, setLocalError] = useState<string | null>(null);

  const can_submit = lines.every(
    (line) => (lots[line.id] ?? '').trim().length > 0 && (next_dates[line.id] ?? '').trim().length > 0,
  );

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) {
      setLocalError(t('doctor_pos.vaccination_lot_all_required'));
      return;
    }
    setLocalError(null);
    await on_submit(
      lines.map((line) => ({
        sale_order_item_id: line.id,
        lot_number: (lots[line.id] ?? '').trim(),
        next_application_date: (next_dates[line.id] ?? '').trim(),
      })),
    );
  };

  const error = local_error ?? error_text;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('doctor_pos.vaccination_lot_modal_title')}</CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('doctor_pos.vaccination_lot_modal_hint')}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {lines.map((line) => (
            <div key={line.id} className="space-y-2 rounded-md border p-2">
              <p className="text-sm font-medium">{line.item_name}</p>
              <div className="space-y-1">
                <Label htmlFor={`vaccination-lot-${line.id}`}>
                  {t('doctor_pos.vaccination_lot_label')}
                </Label>
                <Input
                  id={`vaccination-lot-${line.id}`}
                  autoFocus={lines[0]?.id === line.id}
                  value={lots[line.id] ?? ''}
                  onChange={(e) => setLots((prev) => ({ ...prev, [line.id]: e.target.value }))}
                  disabled={submitting}
                  placeholder={t('doctor_pos.vaccination_lot_placeholder')}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`vaccination-next-date-${line.id}`}>
                  {t('doctor_pos.vaccination_next_date_label')}
                </Label>
                <Input
                  id={`vaccination-next-date-${line.id}`}
                  type="date"
                  min={todayIso()}
                  value={next_dates[line.id] ?? ''}
                  onChange={(e) => setNextDates((prev) => ({ ...prev, [line.id]: e.target.value }))}
                  disabled={submitting}
                />
              </div>
            </div>
          ))}
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit || submitting}
          >
            {submitting ? t('common.loading') : t('doctor_pos.vaccination_lot_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
